import { gql, useMutation } from '@apollo/client';
import { useGeneralSoContext } from 'src/common/context';
import { CreateOfferInput, CreateOfferMutation, CreateOfferMutationVariables } from '../types/types';

/**
 * Create an Offer from a selected package
 * 
 * After user selects a package from calculations, create an offer
 * that will be used for payment processing.
 * 
 * @param soId - Encrypted SO ID
 * @param input - Offer creation input with selected package details
 * @returns Created Offer
 * 
 * @example
 * const [createOfferMutation] = useMutation(createOffer);
 * 
 * const result = await createOfferMutation({
 *   variables: {
 *     soId: encryptedSoId,
 *     input: {
 *       brandCode: 'grawe',
 *       packageCode: 'standard',
 *       calculationId: calculation.calculationId,
 *       sessionId: calculation.sessionId
 *     }
 *   }
 * });
 */
export const CREATE_OFFER_MUTATION = gql`
  mutation CreateOffer($data: CreateOfferInput!) {
    createOffer(data: $data) {
      _id
      offerNumber
      status
      soId
      productTypeId
      departmentCode
      productTypeSpecific {
        brandId {
          code
          name
        }
        shoppingCartId
        building { 
          package { 
            productId 
            code 
            label 
            description 
            propertyType 
          } 
          coverages {
            code
            label
            description
          }
        }
        household { 
          package { 
            productId 
              code 
              label 
              description 
              propertyType 
            } 
            coverages {
            code
            label
            description
          }
          }
        payment { 
          basePremium 
          discount 
          totalPremium 
        }
        newPolicy { 
          newPolicyStartDate 
          policyDuration 
          cancelContract
        }
        attachedDocuments
        termsAndConditions
      }
    }
  }
`;

export function useCreateGeneralOffer() {
  const { setOfferId } = useGeneralSoContext();
  const [mutation, info] = useMutation<CreateOfferMutation, CreateOfferMutationVariables>(CREATE_OFFER_MUTATION);
  const createOffer = async (input: CreateOfferInput) => {
    const { data } = await mutation({
      variables: { data: input },
    });
    if (data?.createOffer) {
      setOfferId(data.createOffer._id);
    }
    return data;
  };
  return [createOffer, info] as const;
}
